import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { casesList } from '@/mocks/cases';
import GallerySection from './components/GallerySection';

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const caseItem = casesList.find((c) => c.id === id);

  if (!caseItem) return <Navigate to="/cases" replace />;

  const currentIndex = casesList.findIndex((c) => c.id === id);
  const prevCase = currentIndex > 0 ? casesList[currentIndex - 1] : null;
  const nextCase = currentIndex < casesList.length - 1 ? casesList[currentIndex + 1] : null;

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-0 md:pt-52 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-3 mb-12 text-xs font-sans-jp text-muted-light">
              <Link to="/" className="hover:text-accent-teal transition-colors duration-200">Home</Link>
              <span>/</span>
              <Link to="/cases" className="hover:text-accent-teal transition-colors duration-200">事例</Link>
              <span>/</span>
              <span className="text-muted-brown">{caseItem.id}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
              <div className="md:col-span-8">
                <div className="flex items-center gap-4 mb-8">
                  <span className="font-garamond text-3xl font-light text-divider">{caseItem.id}</span>
                  <span className="text-xs font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal px-2 py-0.5">
                    {caseItem.tag}
                  </span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl mb-6">
                  {caseItem.title}
                </h1>
                <p className="font-sans-jp text-base text-muted-brown leading-relaxed-jp">
                  {caseItem.subtitle}
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-4 md:pt-16">
                <div className="border-t border-divider pt-5">
                  <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-2">Period</p>
                  <p className="font-sans-jp text-sm text-charcoal">{caseItem.period}</p>
                </div>
                <div className="border-t border-divider pt-5">
                  <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-2">Scope</p>
                  <p className="font-sans-jp text-sm text-charcoal leading-relaxed">{caseItem.scope}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main image */}
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="w-full aspect-[16/7] overflow-hidden">
              <img
                src={caseItem.img}
                alt={caseItem.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Before / After */}
        <section className="py-20 md:py-28 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-divider border border-divider">
              <div className="bg-warm-white p-10 md:p-14">
                <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-6">Before</p>
                <p className="font-serif-jp font-light text-muted-brown tracking-normal-jp leading-relaxed text-lg md:text-xl">
                  {caseItem.before}
                </p>
              </div>
              <div className="bg-charcoal p-10 md:p-14">
                <p className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase mb-6">After</p>
                <p className="font-serif-jp font-light text-warm-white tracking-normal-jp leading-relaxed text-lg md:text-xl">
                  {caseItem.after}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge */}
        <section className="py-20 md:py-28 bg-warm-light">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
              <div className="md:col-span-3">
                <div className="flex items-center gap-4 mb-4">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Challenge</span>
                </div>
                <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl">課題</h2>
              </div>
              <div className="md:col-span-9">
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                  {caseItem.detail.challenge}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Approach */}
        <section className="py-20 md:py-28 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
              <div className="md:col-span-3">
                <div className="flex items-center gap-4 mb-4">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Approach</span>
                </div>
                <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl">アプローチ</h2>
              </div>
              <div className="md:col-span-9">
                <div className="space-y-0 border-t border-divider">
                  {caseItem.detail.approach.map((item, i) => (
                    <div key={i} className="flex items-start gap-6 py-5 border-b border-divider">
                      <span className="font-garamond text-sm text-muted-light flex-shrink-0 w-6">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <p className="font-sans-jp text-sm text-charcoal leading-relaxed-jp">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 md:py-28 bg-warm-light">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
              <div className="md:col-span-5">
                <div className="flex items-center gap-4 mb-6">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Process</span>
                </div>
                <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl">
                  プロセス
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-divider border border-divider">
              {caseItem.detail.process.map((p) => (
                <div key={p.phase} className="bg-warm-light p-10 md:p-12 flex flex-col gap-5">
                  <div>
                    <span className="font-garamond text-xs italic text-muted-light tracking-widest-jp block mb-2">{p.phase}</span>
                    <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-xl">{p.title}</h3>
                  </div>
                  <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <GallerySection gallery={caseItem.detail.gallery} title={caseItem.title} />

        {/* Results */}
        <section className="py-20 md:py-28 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
              <div className="md:col-span-5">
                <div className="flex items-center gap-4 mb-6">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Results</span>
                </div>
                <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl">
                  変化の記録
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-divider border border-divider mb-16">
              {caseItem.detail.results.map((r) => (
                <div key={r.label} className="bg-warm-white p-8 md:p-10 flex flex-col gap-3">
                  <p className="font-sans-jp text-xs text-muted-light tracking-widest-jp">{r.label}</p>
                  <p className="font-serif-jp font-light text-accent-teal tracking-normal-jp text-xl md:text-2xl leading-snug">
                    {r.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-8 md:col-start-3">
                <div className="border-l-2 border-accent-teal pl-10 py-4">
                  <blockquote className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-relaxed text-xl md:text-2xl mb-6">
                    「{caseItem.detail.quote.text}」
                  </blockquote>
                  <p className="font-sans-jp text-xs text-muted-light tracking-widest-jp">
                    — {caseItem.detail.quote.author}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Prev / Next navigation */}
        <section className="py-16 bg-warm-light border-t border-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 gap-4">
              <div>
                {prevCase && (
                  <Link to={`/cases/${prevCase.id}`} className="group flex flex-col gap-3 cursor-pointer">
                    <span className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase flex items-center gap-2">
                      <span className="inline-block transition-transform duration-200 group-hover:-translate-x-1">←</span>
                      Prev
                    </span>
                    <span className="text-xs font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal px-2 py-0.5 w-fit">
                      {prevCase.tag}
                    </span>
                    <p className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-snug group-hover:text-accent-teal transition-colors duration-300 text-sm md:text-base">
                      {prevCase.title}
                    </p>
                  </Link>
                )}
              </div>
              <div className="text-right">
                {nextCase && (
                  <Link to={`/cases/${nextCase.id}`} className="group flex flex-col gap-3 items-end cursor-pointer">
                    <span className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase flex items-center gap-2">
                      Next
                      <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
                    </span>
                    <span className="text-xs font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal px-2 py-0.5 w-fit">
                      {nextCase.tag}
                    </span>
                    <p className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-snug group-hover:text-accent-teal transition-colors duration-300 text-sm md:text-base">
                      {nextCase.title}
                    </p>
                  </Link>
                )}
              </div>
            </div>
            <div className="mt-12 text-center">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-sm font-sans-jp text-muted-brown hover:text-accent-teal transition-colors duration-200 cursor-pointer"
              >
                ← すべての事例に戻る
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-warm-white border-t border-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-3">
                あなたの組織の変化も、ここから始まります。
              </h3>
              <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                まずは30分、現状の「意味」を一緒に整理しましょう。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap flex-shrink-0"
            >
              相談してみる
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
